import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d6tkv8q4of3kuoicn7dg';

const values = [
  { icon: 'ri-heart-line', title: 'Customer First', desc: 'Every decision we make starts with one question: what\'s best for our customer? Your experience is our top priority.' },
  { icon: 'ri-shield-check-line', title: 'Integrity Always', desc: 'Transparent pricing, honest communication, and no hidden fees. We do what we say, every single time.' },
  { icon: 'ri-star-line', title: 'Excellence in Craft', desc: 'We\'ve honed our moving techniques over 10+ years. Every item is handled with skill, care, and professional precision.' },
  { icon: 'ri-community-line', title: 'Community Roots', desc: 'Minneapolis is our home. We\'re deeply committed to the communities and neighborhoods we serve throughout the Twin Cities.' },
  { icon: 'ri-time-line', title: 'Reliable Always', desc: 'We show up on time, every time. Punctuality isn\'t just a value — it\'s a promise we make to every customer.' },
  { icon: 'ri-emotion-happy-line', title: 'Care in Every Move', desc: 'We treat your belongings the same way we\'d treat our own. Careful, gentle, and with genuine respect for what matters to you.' },
];

const milestones = [
  { year: '2015', title: 'TrueCare Founded', desc: 'Started with a single truck and a commitment to honest, careful moving in Minneapolis.' },
  { year: '2017', title: 'BBB A+ Rating', desc: 'Earned our A+ Better Business Bureau accreditation — a reflection of our commitment to quality.' },
  { year: '2019', title: '100+ Moves Milestone', desc: 'Completed over 100 successful moves and expanded our fleet to serve more of the Twin Cities.' },
  { year: '2022', title: '200 Google Reviews', desc: 'Reached 200 five-star Google reviews — a testament to the trust our customers place in TrueCare.' },
  { year: '2024', title: '500+ Moves Completed', desc: 'Crossed 500 total moves, cementing our reputation as Minneapolis\'s most trusted moving company.' },
  { year: '2026', title: 'Continuing to Grow', desc: 'Expanding our team and service areas while maintaining the personal care of a family-owned business.' },
];

const trustFactors = [
  { icon: 'ri-shield-check-line', label: 'Licensed & Insured' },
  { icon: 'ri-award-line', label: 'A+ BBB Rated' },
  { icon: 'ri-star-fill', label: '231 Google Reviews' },
  { icon: 'ri-history-line', label: '10+ Years Experience' },
  { icon: 'ri-heart-line', label: 'Family-Owned' },
  { icon: 'ri-price-tag-3-line', label: 'No Hidden Fees' },
  { icon: 'ri-thumb-up-line', label: '500+ Moves Done' },
  { icon: 'ri-map-pin-line', label: '24+ Cities Served' },
];

const useVisible = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
};

const AboutPage = () => {
  const [pageVisible, setPageVisible] = useState(false);
  const hero = useVisible();
  const story = useVisible();
  const vals = useVisible();
  const timeline = useVisible();
  const trust = useVisible();
  const [formState, setFormState] = useState({ name: '', phone: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setTimeout(() => setPageVisible(true), 100);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ ...formState });
      await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ fontFamily: '"Inter", sans-serif' }}>
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-24 bg-hero-gradient overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://public.readdy.ai/ai/img_res/06667982a9ecbf887b19c7da63cdeeba.jpg"
              alt="TrueCare Moving Services team"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/80 to-navy/40"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className={`transition-all duration-700 ${pageVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="inline-flex items-center gap-2 bg-teal/20 border border-teal/30 rounded-full px-4 py-2 mb-6">
                <div className="w-1.5 h-1.5 bg-teal rounded-full"></div>
                <span className="text-teal text-xs font-body tracking-widest uppercase">About TrueCare Moving Services</span>
              </div>
              <h1 className="font-heading font-800 text-white text-5xl lg:text-6xl mb-5 leading-tight">
                Moving Minneapolis<br />
                <span className="text-teal">Forward</span>
              </h1>
              <p className="text-white/70 font-body text-xl leading-relaxed mb-8 max-w-xl">
                A family-owned Minneapolis moving company built on integrity, experience, and genuine care for every customer we serve.
              </p>
              <div className="flex flex-wrap gap-4">
                {['Family-Owned & Local', '10+ Years in Minneapolis', '231 Five-Star Reviews'].map((b) => (
                  <div key={b} className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                    <i className="ri-check-line text-teal text-sm"></i>
                    <span className="text-white font-body text-sm font-medium">{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Story */}
        <section ref={story.ref} className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className={`transition-all duration-700 ${story.visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=family%20owned%20business%20team%20of%20professional%20movers%20in%20Minneapolis%20Minnesota%2C%20father%20and%20son%20moving%20company%20owners%20standing%20proudly%20next%20to%20their%20branded%20moving%20truck%2C%20sunny%20residential%20neighborhood%2C%20warm%20family%20atmosphere%2C%20professional%20uniforms%2C%20high%20quality%20photography%2C%20award%20winning&width=600&height=450&seq=about002&orientation=landscape"
                    alt="TrueCare family-owned moving company founders"
                    className="w-full h-96 object-cover object-top"
                  />
                </div>
              </div>
              <div className={`transition-all duration-700 delay-200 ${story.visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
                <div className="inline-flex items-center gap-2 mb-4">
                  <div className="h-0.5 w-8 bg-teal"></div>
                  <span className="text-teal font-body text-xs tracking-widest uppercase">Our Story</span>
                </div>
                <h2 className="font-heading font-800 text-navy text-4xl mb-6 leading-tight">
                  Built on Trust, Grown on Reputation
                </h2>
                <div className="space-y-4 text-charcoal/65 font-body text-base leading-relaxed">
                  <p>
                    TrueCare Moving Services LLC was founded in Minneapolis with one simple belief: that every customer deserves a moving company they can truly trust. No hidden fees. No careless handling. No broken promises.
                  </p>
                  <p>
                    What started as a single truck and a family commitment has grown into one of Minneapolis&apos;s most respected moving companies — with a proven track record, an A+ BBB rating, and 231 five-star Google reviews from real customers across the Twin Cities.
                  </p>
                  <p>
                    We&apos;ve moved everything from studio apartments to large commercial offices, from first homes to retirement moves. Through every job, our commitment has never wavered: treat people right, handle their belongings with genuine care, and deliver a premium experience at an honest price.
                  </p>
                  <p>
                    Because to us, <strong className="text-navy">it&apos;s not just a move. It&apos;s your life.</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission / Values */}
        <section ref={vals.ref} className="py-24 bg-cream">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className={`text-center mb-16 transition-all duration-700 ${vals.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="inline-flex items-center gap-2 mb-4">
                <div className="h-0.5 w-8 bg-teal"></div>
                <span className="text-teal font-body text-xs tracking-widest uppercase">Our Mission & Values</span>
                <div className="h-0.5 w-8 bg-teal"></div>
              </div>
              <h2 className="font-heading font-800 text-navy text-4xl lg:text-5xl mb-4">What We Stand For</h2>
              <p className="text-charcoal/60 font-body text-lg max-w-xl mx-auto">
                These aren&apos;t just words on a wall. They&apos;re the principles that guide every move we make.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((v, i) => (
                <div
                  key={i}
                  className={`bg-white rounded-2xl p-7 border border-transparent hover:border-teal/25 transition-all duration-700 group ${vals.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: vals.visible ? `${i * 80}ms` : '0ms' }}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-navy/5 group-hover:bg-teal/15 rounded-xl mb-4 transition-colors">
                    <i className={`${v.icon} text-navy group-hover:text-teal text-xl transition-colors`}></i>
                  </div>
                  <h3 className="font-heading font-700 text-navy text-lg mb-2">{v.title}</h3>
                  <p className="text-charcoal/60 font-body text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section ref={timeline.ref} className="py-24 bg-navy">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className={`text-center mb-16 transition-all duration-700 ${timeline.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="inline-flex items-center gap-2 mb-4">
                <div className="h-0.5 w-8 bg-teal"></div>
                <span className="text-teal font-body text-xs tracking-widest uppercase">Our Journey</span>
                <div className="h-0.5 w-8 bg-teal"></div>
              </div>
              <h2 className="font-heading font-800 text-white text-4xl lg:text-5xl mb-4">10+ Years of Trust</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {milestones.map((m, i) => (
                <div
                  key={i}
                  className={`bg-white/5 border border-white/10 hover:border-teal/30 rounded-2xl p-6 transition-all duration-700 ${timeline.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: timeline.visible ? `${i * 80}ms` : '0ms' }}
                >
                  <div className="inline-block bg-teal/20 text-teal font-heading font-800 text-lg px-3 py-1 rounded-lg mb-4">{m.year}</div>
                  <h3 className="font-heading font-700 text-white text-base mb-2">{m.title}</h3>
                  <p className="text-text-muted font-body text-sm leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Factors */}
        <section ref={trust.ref} className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className={`text-center mb-16 transition-all duration-700 ${trust.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="inline-flex items-center gap-2 mb-4">
                <div className="h-0.5 w-8 bg-teal"></div>
                <span className="text-teal font-body text-xs tracking-widest uppercase">Why We&apos;re Trusted</span>
                <div className="h-0.5 w-8 bg-teal"></div>
              </div>
              <h2 className="font-heading font-800 text-navy text-4xl lg:text-5xl mb-4">The TrueCare Difference</h2>
              <p className="text-charcoal/60 font-body text-lg max-w-xl mx-auto">
                We don&apos;t just move boxes. We earn trust through consistent excellence and genuine care.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {trustFactors.map((t, i) => (
                <div
                  key={i}
                  className={`flex flex-col items-center text-center py-8 px-5 bg-cream rounded-2xl hover:bg-teal/5 border border-transparent hover:border-teal/25 transition-all duration-700 group ${trust.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: trust.visible ? `${i * 60}ms` : '0ms' }}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-navy/8 group-hover:bg-teal/15 rounded-xl mb-4 transition-colors">
                    <i className={`${t.icon} text-navy group-hover:text-teal text-xl transition-colors`}></i>
                  </div>
                  <span className="font-heading font-700 text-navy text-sm">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-navy relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-96 h-96 border border-teal/10 rounded-full"></div>
            <div className="absolute -bottom-32 -left-32 w-96 h-96 border border-teal/10 rounded-full"></div>
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-heading font-800 text-white text-4xl lg:text-5xl mb-5 leading-tight">
              Experience the <span className="text-teal">TrueCare</span> Difference
            </h2>
            <p className="text-text-muted font-body text-lg mb-10 max-w-2xl mx-auto">
              Join hundreds of Minneapolis families and businesses who trust TrueCare for their most important moves.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/#quote" className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-navy font-heading font-700 px-10 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-file-text-line"></i>Get Free Quote
              </Link>
              <a href="tel:+16123674481" className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-teal text-white hover:text-teal font-heading font-600 px-10 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill"></i>Call (612) 367-4481
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
